import { Routes, Route, useLocation, Navigate, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { Dialog, Toast } from 'antd-mobile';
import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { AddOutline } from 'antd-mobile-icons';
import Home from './pages/Home';
import Customers from './pages/Customers';
import CustomerDetail from './pages/CustomerDetail';
import CustomerForm from './pages/CustomerForm';
import SaleForm from './pages/SaleForm';
import Search from './pages/Search';
import Settings from './pages/Settings';
import { useAuthStore } from './stores/authStore';
import Login from './pages/Login';
import { dispatchBackHandler } from './hooks/useBackHandler';
import useHapticFeedback from './hooks/useHapticFeedback';
import { NavGlyph } from './components/Icon';

const Statistics = lazy(() => import('./pages/Statistics'));
const CategoryManage = lazy(() => import('./pages/CategoryManage'));
const PriceLevelManage = lazy(() => import('./pages/PriceLevelManage'));

type TabKind = 'home' | 'customers' | 'record' | 'search' | 'profile';

const tabs: Array<{ key: string; title: string; kind: TabKind }> = [
  { key: '/', title: '首页', kind: 'home' },
  { key: '/customers', title: '客户', kind: 'customers' },
  { key: 'record', title: '记一笔', kind: 'record' },
  { key: '/search', title: '搜索', kind: 'search' },
  { key: '/settings', title: '我的', kind: 'profile' }
];

const tabPaths = ['/', '/customers', '/search', '/settings'];

function secondaryDestination(pathname: string, search: string) {
  const params = new URLSearchParams(search);
  const source = params.get('from');
  const customerId = params.get('customerId');
  const returnTo = params.get('returnTo');
  const statisticsFrom = params.get('statisticsFrom') === 'settings' ? 'settings' : 'home';

  if (returnTo?.startsWith('/')) return returnTo;
  if (source === 'search') return '/search';
  if (source === 'statistics') return `/statistics?from=${statisticsFrom}`;
  if (source === 'customers') {
    const listParams = new URLSearchParams();
    const village = params.get('village');
    const listFrom = params.get('listFrom');
    if (village) listParams.set('village', village);
    if (listFrom === 'search' || listFrom === 'statistics') listParams.set('from', listFrom);
    if (listFrom === 'statistics') listParams.set('statisticsFrom', statisticsFrom);
    const query = listParams.toString();
    return `/customers${query ? `?${query}` : ''}`;
  }
  if (source === 'customer' && customerId) return `/customer/${customerId}`;
  if (pathname === '/statistics') return source === 'settings' ? '/settings' : '/';
  if (/^\/settings\/categories\/[^/]+\/price-levels$/.test(pathname)) {
    return '/settings/categories';
  }
  if (pathname.startsWith('/customer/')) return '/customers';
  if (pathname.startsWith('/settings/')) return '/settings';
  return '/';
}

function IOSBottomTab() {
  const location = useLocation();
  const navigate = useNavigate();

  return (
    <motion.div
      className="wine-tabbar"
      initial={{ y: 100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] }}
    >
      {tabs.map((tab) => {
        const isRecord = tab.key === 'record';
        const isActive = location.pathname === tab.key;
        return (
          <motion.button
            key={tab.key}
            type="button"
            aria-label={tab.title}
            aria-current={isActive ? 'page' : undefined}
            onClick={() => isRecord ? navigate('/sale') : navigate(tab.key, { replace: true })}
            className={`wine-tabbar__item${isActive ? ' wine-tabbar__item--active' : ''}${isRecord ? ' wine-tabbar__item--record' : ''}`}
            whileTap={{ scale: 0.9 }}
            data-haptic={isRecord ? 'medium' : 'selection'}
          >
            <span className="wine-tabbar__icon">
              {isRecord ? <AddOutline /> : <NavGlyph kind={tab.kind as Exclude<TabKind, 'record'>} active={isActive} />}
            </span>
            <span>{tab.title}</span>
          </motion.button>
        );
      })}
    </motion.div>
  );
}

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const prevPathRef = useRef(location.pathname);
  const { token, isReady, initAuth } = useAuthStore();
  const [initDone, setInitDone] = useState(false);
  const lastExitBackRef = useRef(0);

  useHapticFeedback();

  useEffect(() => {
    initAuth();
    setInitDone(true);
  }, [initAuth]);

  useEffect(() => {
    prevPathRef.current = location.pathname;
    lastExitBackRef.current = 0;
  }, [location.pathname]);

  useEffect(() => {
    const handleBack = async () => {
      if (document.querySelector('.adm-dialog')) {
        Dialog.clear();
        return;
      }

      if (await dispatchBackHandler()) return;

      const isExitPage = location.pathname === '/' || location.pathname === '/login';
      if (isExitPage) {
        const now = Date.now();
        if (now - lastExitBackRef.current <= 2000) {
          CapacitorApp.exitApp();
        } else {
          lastExitBackRef.current = now;
          Toast.show({ content: '再按一次退出应用' });
        }
        return;
      }

      const hasExplicitSource = new URLSearchParams(location.search).has('from');
      if (tabPaths.includes(location.pathname) && !hasExplicitSource) {
        navigate('/', { replace: true });
        return;
      }

      navigate(secondaryDestination(location.pathname, location.search), { replace: true });
    };

    const isNative = Capacitor.isNativePlatform();
    const listener = isNative
      ? CapacitorApp.addListener('backButton', handleBack)
      : null;

    return () => {
      listener?.then((handle) => handle.remove());
    };
  }, [location.pathname, location.search, navigate]);

  if (!isReady || !initDone) {
    return (
      <div
        style={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--color-foreground-secondary)'
        }}
      >
        加载中...
      </div>
    );
  }

  const isLoginPage = location.pathname === '/login';
  if (!token && !isLoginPage) {
    return <Navigate to="/login" replace />;
  }
  if (token && isLoginPage) {
    return <Navigate to="/" replace />;
  }

  const isTabPage = tabPaths.includes(location.pathname);
  const isTabSwitch =
    isTabPage && tabPaths.includes(prevPathRef.current);

  return (
    <div
      className={`wine-app-shell${isTabPage ? ' wine-app-shell--tab' : ' ios-app-no-tab'}`}
    >
      <div className="ios-scroll-view wine-app-scroll">
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            initial={isTabSwitch ? false : { opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={isTabSwitch ? { opacity: 1 } : { opacity: 0, x: -20 }}
            transition={
              isTabSwitch
                ? { duration: 0 }
                : { duration: 0.2, ease: [0.25, 0.1, 0.25, 1] }
            }
            className="wine-route"
          >
            <Suspense fallback={<div className="ios-page" style={{ display: 'grid', placeItems: 'center' }}>加载中...</div>}>
              <Routes location={location}>
              <Route path="/" element={<Home />} />
              <Route path="/customers" element={<Customers />} />
              <Route path="/customer/:id" element={<CustomerDetail />} />
              <Route path="/customer/form" element={<CustomerForm />} />
              <Route path="/customer/form/:id" element={<CustomerForm />} />
              <Route path="/sale" element={<SaleForm />} />
              <Route path="/sale/:id/edit" element={<SaleForm />} />
              <Route path="/search" element={<Search />} />
              <Route path="/statistics" element={<Statistics />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/settings/categories" element={<CategoryManage />} />
              <Route path="/settings/categories/:categoryId/price-levels" element={<PriceLevelManage />} />
              <Route path="/login" element={<Login />} />
              <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </motion.div>
        </AnimatePresence>
      </div>
      {isTabPage && <IOSBottomTab />}
    </div>
  );
}

export default App;
